import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ColActionId,
  DocumentInfo,
  DocumentPermission,
  DocumentUpdateResponse,
  DownloadResponse,
  EditorEditItem,
  EditorLink,
  NavigationData, StoreTemplateStatus,
  TemplateGroup,
  ViewMode
} from '@pt/pt-models';
import { BehaviorSubject, Observable, of, take } from 'rxjs';
import { AiAssessmentsService, FolderService } from '@pt/api-client';
import { ActionsSubject, Store } from '@ngrx/store';
import { AiAssessmentActions, NgrxWidgetActions, NgrxWidgetSelectors } from '../../store';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { WidgetDialogService } from '../../services/widget-dialog.service';
import {
  AiAssessmentDocUpdateRequest,
  CommonDocumentEditorDetails,
  WorkspaceMoveRequest
} from '@primatoo-ms-organization';
import { Folder } from '@ms-organization-entities';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { EditorInternalStatus } from '../../editor-internal-status';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ChapterStatus } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterStatus';
import { SetChapterStatusRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SetChapterStatusRequest';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../../services';
import { DownloadRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/DownloadRequest';
import { AppLinkBuilder } from '../../../utils';
import {
  ListTemplateChapterResponse
} from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { ActivatedRoute, Router } from '@angular/router';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { EditorStoreService } from '../../services';
import { DocResourceStatus } from 'it/primatoo/openapi/api/org/schemas/common/DocResourceStatus';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import { Confirm } from '../../../decorators';


@UntilDestroy()
@Component({
  selector: 'pt-ai-ass-editor',
  template: `
    <pt-editor-layout
      class="pt-editor-layout"
      [showEditor]="showEditor"
      [showSummary]="false"
      [showToggleSummary]="false"
      [showNotification]="editorInternalStatus.showNotification"
      [showSidebar]="editorInternalStatus.showSidebar"
      (toggleSidebar)="editorInternalStatus.toggleShowSidebar()">

      <ng-container notification>
        <pt-editor-notification
          class='editor-notification'
          [noChapterPermission]="editorInternalStatus.noChapterPermission"
          [warnings]="warnings$ | async"
          [userCheck]="editorInternalStatus.userCheck"
          [autosave]='editorInternalStatus.isAutoSaveActive'
          [message]='editorInternalStatus.i18nErrorMessage | translate'
          (removeUserCheck)="removeUserCheck()">
        </pt-editor-notification>
      </ng-container>

      <ng-container header>
        <pt-template-header
          class='editor-header'
          [loading]="!showEditor"
          [docInfo]="docInfo$ | async"
          [documentPermission]="documentPermission$ | async"

          [isDraft]='editorInternalStatus.isDraft'
          [descriptionButton]='editorInternalStatus.descriptionButton'
          [headerEntityName]='documentInfo?.headerEntityName | translate'
          [headerTitle]='documentInfo?.name'
          [headerIconKey]='documentInfo?.headerIconKey'
          [templateStatusLabel]='editorInternalStatus?.details?.statusLabel'
          [description]='editorInternalStatus?.details?.description'
          [isPublishedDocument]='editorInternalStatus?.isPublishedDocument'
          (updateDescription)='updateDocumentDescription($event)'
          (enabledEditTreatment)='enabledEditDocument($event)'>
        </pt-template-header>
      </ng-container>

      <ng-container body>
        <div *ngIf="localListChapters?.length === 1" style="height: 100%; overflow: auto; margin-bottom: 10px;">
          <pt-template-editor-groups
            class='editor-body'
            *ngIf="currentChapter"
            [documentEditorDetails]="documentEditorDetails"
            [documentPermission]="documentPermission$ | async"
            [isDraft]="editorInternalStatus.isDraft"
            [editorReady]="editorInternalStatus.editorReady"
            [activeChapter]="getActiveChapterInfo$ | async"
            [viewMode]="viewMode$ | async"
            [activedEditWidgetId]='editorInternalStatus?.actualEditModeWidgetId'
            [selectedGroup]='selectedChapterGroup'
            [lockAcquired]='editorInternalStatus?.lockAcquired'
            [status]='status$ | async'
            [getChapterResponse]='currentChapter'
            (changeChapterStatus)="changeChapterStatus($event)"
            (changeViewMode)="changeViewMode($event)"
            (changeGroup)='selectChapterGroup($event)'>
          </pt-template-editor-groups>
        </div>

        <ng-container *ngIf="localListChapters?.length > 1">
          <ul ngbNav #nav="ngbNav"
              [(activeId)]="active" [destroyOnHide]="false" (navChange)="onNavChange($event?.nextId)"
              class="nav-tabs">
            <ng-container *ngFor="let c of localListChapters; index as i">
              <li [ngbNavItem]="i"
                  [destroyOnHide]="true"
                  [disabled]="!editorInternalStatus?.editorReady">
                <button ngbNavLink class="d-flex align-items-center">
                  <pt-tab-chapter [chapterId]="c.id" [chapters]="listChapters"></pt-tab-chapter>
                </button>
                <ng-template ngbNavContent>
                  <pt-template-editor-groups
                    class='editor-body'
                    *ngIf="currentChapter"
                    [documentEditorDetails]="documentEditorDetails"
                    [isDraft]="editorInternalStatus.isDraft"
                    [editorReady]="editorInternalStatus.editorReady"
                    [activeChapter]="getActiveChapterInfo$ | async"
                    [viewMode]="viewMode$ | async"
                    [activedEditWidgetId]='editorInternalStatus?.actualEditModeWidgetId'
                    [selectedGroup]='selectedChapterGroup'
                    [lockAcquired]='editorInternalStatus?.lockAcquired'
                    [status]='status$ | async'
                    [getChapterResponse]='currentChapter'
                    (changeChapterStatus)="changeChapterStatus($event)"
                    (changeViewMode)="changeViewMode($event)"
                    (changeGroup)='selectChapterGroup($event)'>
                  </pt-template-editor-groups>
                </ng-template>
              </li>
            </ng-container>
          </ul>
          <div [ngbNavOutlet]="nav" class="editor-body-content"></div>
        </ng-container>
      </ng-container>


      <ng-container sidebar>
        <pt-editor-sidebar
          class='editor-sidebar'
          [documentPermission]="documentPermission$ | async"
          [documentInfo]="docInfo$ | async"
          [documentEditorDetails]="editorInternalStatus.details"
          [selectedGroup]='selectedChapterGroup'

          [permissionDialog]="editorInternalStatus?.permissionDialog"
          [nameButton]='editorInternalStatus?.nameButton'
          [internalIdButton]='editorInternalStatus?.internalIdButton'
          [folderButton]='editorInternalStatus?.folderButton'

          [foldersNavigationData]='folders'
          [folderId]='editorInternalStatus?.details?.parentId'


          [status]='status$ | async'
          [previewLoading]='editorInternalStatus.loadingPreview'
          [wordLoading]='editorInternalStatus.loadingWord'
          [selectedTemplateGroup]='selectedChapterGroup'
          [links]='links'
          (downloadWord)="downloadWord()"
          (preview)='previewDocument()'
          (enabledEditDocument)='enabledEditDocument($event)'
          (updateDocument)='updateDocument($event)'
          (documentActionEvent)="documentActionEvent($event)">

          <ng-container panelResult>
            <pt-ai-result
              [autoReload]="false"
              [docId]="documentInfo?.docId"

              [requireReload]="requireReloadResult$ | async">
            </pt-ai-result>
          </ng-container>

          <ng-container panelGroup>

            <pt-template-complete-status
              [status]='status$ | async'>
            </pt-template-complete-status>

            <pt-editor-sidebar-panel-group
              [chapter]="currentChapter"
              [status]='status$ | async'
              [selectedGroup]='selectedChapterGroup'
              (selectChapterGroup)='selectChapterGroup($event)'>
            </pt-editor-sidebar-panel-group>

          </ng-container>
        </pt-editor-sidebar>
      </ng-container>
    </pt-editor-layout>

    <ng-template #widgetNotifications let-modal>
      <pt-widget-notification
        [activeChapter]="getActiveChapterInfo$ | async"
        [title]='documentInfo?.name'
        [notifications]='widgetNotifications$ | async'
        (closeDialog)='closeWidgetNotificationDialog()'>
      </pt-widget-notification>
    </ng-template>
  `,
  styleUrls: [`document-editor.component.scss`]
})
export class AiAssEditorComponent implements OnChanges, OnDestroy {
  @Input() listChapters: Array<Chapter> = [];

  /**
   * Attenzione non utilizzarlo se non nell'ONCHANGE usare this.editorInternalStatus.details
   */
  @Input() documentEditorDetails: CommonDocumentEditorDetails;
  @Input() documentInfo: DocumentInfo;
  @Input() language: string;
  @Output() goBack = new EventEmitter();
  @Output() duplicateDocument = new EventEmitter();

  @ViewChild('widgetNotifications', {static: true}) widgetNotificationsTemplateRef: TemplateRef<unknown>;
  public currentChapter?: GetChapterResponse;
  public readonly editorInternalStatus: EditorInternalStatus;
  public folders: NavigationData[];
  public selectedChapterGroup: TemplateGroup;
  private $listChapters: BehaviorSubject<Chapter[]> = new BehaviorSubject<Chapter[]>([]);
  public localListChapters: Array<Chapter> = [];

  public getActiveChapterInfo$: Observable<Chapter> = this.store.select(NgrxWidgetSelectors.getActiveChapterInfo);
  public documentPermission$: Observable<AvailableOperations> = this.store.select(NgrxWidgetSelectors.getDocumentPermission)
  public warnings$: Observable<TreatmentWarning[]> = this.store.select(NgrxWidgetSelectors.warnings);
  public viewMode$: Observable<ViewMode> = this.store.select(NgrxWidgetSelectors.getViewMode);
  public requireReloadResult$: Observable<boolean> = this.store.select(NgrxWidgetSelectors.requireReloadResult);
  public status$: Observable<StoreTemplateStatus> = this.store.select(NgrxWidgetSelectors.getWidgetsStatus);
  public docInfo$: Observable<DocumentInfo> = this.store.select(NgrxWidgetSelectors.getDocInfo);
  public widgetNotifications$ = this.store.select(NgrxWidgetSelectors.widgetNotifications);
  public status: StoreTemplateStatus;
  public currentActiveChapter: Chapter;
  public links: EditorLink[] = [];
  public active = 0;
  public showEditor: boolean;
  private widgetNotificationDialog: NgbModalRef;
  public viewMode: ViewMode = ViewMode.LIST;

  constructor(
    public service: AiAssessmentsService,
    private store: Store,
    public widgetDialogService: WidgetDialogService,
    private translateService: TranslateService,
    private folderService: FolderService,
    public actionsSubject: ActionsSubject,
    private dialogService: DialogService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    this.editorInternalStatus = new EditorInternalStatus('aiAssessment');
    this.loadDocumentProperties();

    this.store.select(PtCoreSelectors.organization).pipe(untilDestroyed(this),).subscribe((org) => {
      this.editorInternalStatus.setInternalIdEditable(org.editInternalIdEnable);
    });

    this.$listChapters.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      withLatestFrom(this.activatedRoute.queryParams)
    ).subscribe(([, params]) => {
      const activeChapter = EditorStoreService.getChapterSelectorFromAlias(params['c']);
      const activeChapterId = params['id'];

      let actualChapterIndex = 0;
      if (this.localListChapters) {
        if (activeChapter) {
          this.localListChapters.forEach((l, index) => {
            if (Number.parseInt(activeChapterId)) {
              if (l.id == activeChapterId && l.selector == activeChapter) {
                // prendo il primo indice con quel selettore
                actualChapterIndex = index;
              }
            } else {
              if (l.selector == activeChapter) {
                // prendo il primo indice con quel selettore
                actualChapterIndex = index;
              }
            }

          });
          // lo 0 si carica di default
        } else {
          let findPreferred = false;
          this.localListChapters.find((l, index) => {
            if (l.preferred && !findPreferred) {

              findPreferred = true;
              // prendo il primo indice con quel selettore
              actualChapterIndex = index;

            }
          });
        }
      }
      this.onNavChange(actualChapterIndex);
    });

    this.getActiveChapterInfo$.pipe(
      untilDestroyed(this)
    ).subscribe((chapter: Chapter) => {
      this.currentActiveChapter = JSON.parse(JSON.stringify(chapter));
    });

    this.viewMode$.pipe(
      untilDestroyed(this),
    ).subscribe((viewMode: ViewMode) => {
      this.viewMode = viewMode;
      if (this.currentChapter) {
        if (!this.selectedChapterGroup) {
          this.selectChapterGroup(this.currentChapter.payload.groups[0]);
        } else {
          this.selectChapterGroup(this.selectedChapterGroup);
        }
      }
    });

    let timeout = undefined;
    this.actionsSubject.pipe(
      ofType(NgrxWidgetActions.upsertWidgetEnabled),
      untilDestroyed(this),
      map(t => t.request)
    ).subscribe(() => {

      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      }

      timeout = setTimeout(() => {
        this.status$.pipe(take(1)).subscribe((s: StoreTemplateStatus) => {
          this.status = { ...s };
          this.editorInternalStatus.changeStoreTemplateStatus(this.status);
          this.checkInitializedTemplateStatus(true);
        })
      }, 1000)
    })


    this.actionsSubject.pipe(
      ofType(NgrxWidgetActions.initializeChapterSuccess),
      untilDestroyed(this),
      map(t => t.chapter)
    ).subscribe((chapter: GetChapterResponse) => {
      if (chapter) {
        this.currentChapter = JSON.parse(JSON.stringify(chapter));
        this.editorInternalStatus.changeChapter(this.currentChapter);

        if (this.viewMode === 'TAB') {
          this.selectChapterGroup(this.currentChapter.payload.groups[0]);
        } else {
          this.selectedChapterGroup = undefined;
        }

        this.showEditor = true;
      }
    });

    this.actionsSubject.pipe(
      ofType(NgrxWidgetActions.reloadEditorDetailsSuccess),
      untilDestroyed(this),
      map(a => a.details),
    ).subscribe((details: CommonDocumentEditorDetails) => {
      if (details) {
        this.editorInternalStatus.changeDetails(details);
        this.updateLink(details);
      }
    });

    this.status$.pipe(
      untilDestroyed(this)
    ).subscribe((status: StoreTemplateStatus) => {
      this.status = status;
      this.editorInternalStatus.changeStoreTemplateStatus(this.status);
      this.checkInitializedTemplateStatus(false);
    });

    this.actionsSubject.pipe(
      ofType(
        AiAssessmentActions.reopenAiAssessmentSuccess,
      ),
      untilDestroyed(this),
    ).subscribe(() => {
      window.location.reload();
    });

    this.actionsSubject.pipe(
      ofType(
        AiAssessmentActions.deleteAiAssessmentSuccess,
        AiAssessmentActions.publishAiAssessmentSuccess,
        AiAssessmentActions.validateAiAssessmentSuccess
      ),
      untilDestroyed(this),
    ).subscribe(() => {
      this.goBack.emit()
    });

    this.actionsSubject.pipe(
      ofType(
        AiAssessmentActions.duplicateAiAssessmentSuccess,
      ),
      map(m => m.response),
      untilDestroyed(this),
    ).subscribe(() => {
      setTimeout(() => {
        window.location.reload()
      }, 1000);
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(NgrxWidgetActions.cleanAll());
    this.closeWidgetNotificationDialog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.language && changes.language.currentValue) {
      this.store.dispatch(NgrxWidgetActions.setLanguage({language: changes.language.currentValue}));
    }

    if (changes.documentEditorDetails && changes.documentEditorDetails.currentValue) {
      this.editorInternalStatus.changeDetails(changes.documentEditorDetails.currentValue);
      this.updateLink(this.editorInternalStatus.details);
    }

    if (changes.listChapters) {
      if (this.localListChapters.length === 0) {
        this.localListChapters = changes.listChapters.currentValue;
        this.$listChapters.next(this.localListChapters);
      }
    }
  }

  getUpdatedChapter(chapter: Chapter): Chapter | undefined {
    return this.listChapters?.find(c => c.id === chapter.id);
  }

  public onChangeLock($event: boolean) {
    this.editorInternalStatus.setLockAcquired($event);
  }

  public onChangeDocumentPermission($event: DocumentPermission) {
    this.editorInternalStatus.setAiAssessmentPermission($event);
    this.checkInitializedTemplateStatus(true);
  }

  public closeWidgetNotificationDialog() {
    if (this.widgetNotificationDialog) {
      this.widgetNotificationDialog.close();
      this.widgetNotificationDialog = null;
    }
  }

  public onNavChange(currentId: number) {
    if (this.localListChapters.length > currentId) {
      this.service.getDocumentTemplateChapters(this.documentInfo.docId).subscribe(
        (chapters) => {
          const currentChapterId = this.localListChapters[currentId].id;
          const updatedChapter: Chapter = chapters.chapters.find(c => c.id === currentChapterId);
          if (updatedChapter) {
            this.currentChapter = undefined;
            this.editorInternalStatus.changeChapter(undefined);

            this.store.dispatch(NgrxWidgetActions.clearWidgetNotifications());
            this.store.dispatch(NgrxWidgetActions.clearDocumentEditorNotifications());
            this.store.dispatch(NgrxWidgetActions.forceReloadedResult());
            this.store.dispatch(NgrxWidgetActions.loadChapter({
              docId: this.editorInternalStatus.details.docId,
              chapter: updatedChapter
            }))
            this.active = currentId;
            this.router.navigate(
              [],
              {
                relativeTo: this.activatedRoute,
                queryParams: {c: EditorStoreService.getChapterSelectorAlias(updatedChapter.selector)},
                queryParamsHandling: 'merge', // remove to replace all query params by provided
              }
            );
          }
        });
    }
  }

  /**
   * Queste API andrebbero nel selettore (sidebar) e non qui
   */
  public loadDocumentProperties() {
    this.folders = null;
    this.folderService.findAllFolders().subscribe(
      (folders: Folder[]) => {
        if (folders) {
          this.folders = folders.map((folder: Folder) => {
            return {
              id: folder.id,
              name: folder.name,
              image: 'folder.svg',
              parentId: folder.parentId,
              auth: true
            } as NavigationData;
          });
        }
      }
    );
  }

  public selectChapterGroup(group: TemplateGroup) {
    this.selectedChapterGroup = group;
  }

  public removeUserCheck() {
    const doc: CommonDocumentEditorDetails = this.editorInternalStatus.details;
    if (this.editorInternalStatus.documentPermission?.writeAccess) {
      if (doc.userCheck) {
        this.store.dispatch(NgrxWidgetActions.userCheckTrueInDraftDocument({docId: doc?.docId}));
      }
    }
  }

  public updateDocumentDescription(description: string) {
    const request = {
      description: description,
      internalId: this.editorInternalStatus?.details?.internalId,
      name: this.editorInternalStatus?.details?.name,
      latestChange: EditorEditItem.DESCRIPTION
    } as DocumentUpdateResponse;
    this.updateDocument(request);
  }

  public updateDocument(documentUpdateResponse: DocumentUpdateResponse) {
    switch (documentUpdateResponse.latestChange) {
      case EditorEditItem.DESCRIPTION:
      case EditorEditItem.INTERNAL_ID:
      case EditorEditItem.NAME: {
        const request: AiAssessmentDocUpdateRequest = {
          description: documentUpdateResponse.description,
          internalId: documentUpdateResponse.internalId,
          name: documentUpdateResponse.name,
        };
        this.store.dispatch(
          AiAssessmentActions.updateAiAssessment({
            id: this.editorInternalStatus?.details?.docId,
            request: request
          }));
        break;
      }
      case EditorEditItem.FOLDER:
        this.store.dispatch(
          AiAssessmentActions.moveAiAssessment({
            id: this.editorInternalStatus?.details?.docId,
            workspaceMoveRequest: {
              parentId: documentUpdateResponse.parentId
            } as WorkspaceMoveRequest
          }));
        break;
    }
  }

  public enabledEditDocument(item: EditorEditItem) {
    if (item) {
      this.store.dispatch(NgrxWidgetActions.setEditorEditItemState({item}));
    } else {
      this.store.dispatch(NgrxWidgetActions.resetEditorEditItemState());
    }
  }

  public documentActionEvent(action: ColActionId) {
    switch (action) {
      case ColActionId.delete:
        this.deleteDocument();
        break;
      case ColActionId.download:
        this.previewDocument();
        break;
      case ColActionId.publish:
        this.publishDocument();
        break;
      case ColActionId.reopen:
        this.reopenDocument();
        break;
      case ColActionId.validate:
        this.validateDocument();
        break;
      case ColActionId.duplicate:
        this.duplicateDocument.emit();
        break;
      case ColActionId.snapshot:
        this.snapshotDocument();
        break;
    }
  }

  private snapshotDocument() {
    const docId = this.editorInternalStatus.details.docId;
    const docName = this.editorInternalStatus.details.name;
    if (docId && docName) {
      this.dialogService.runCreateSnapshotDialog(docName).subscribe(
        (request?: ValidateDocumentRequest) => {
          if (request) {
            this.service.snapshot(docId, request).subscribe(() => {
              this.store.dispatch(PtCoreActions.showOperationSuccessNotification());
            }, () => {
              this.store.dispatch(PtCoreActions.showOperationFailNotification());
            });
          }
        }
      );
    }
  }

  public publishDocument() {
    this.store.dispatch(AiAssessmentActions.publishAiAssessmentWithConfirm({
      docId: this.editorInternalStatus.details.docId
    }));
  }

  public validateDocument() {
    this.store.dispatch(AiAssessmentActions.validateAiAssessmentWithConfirm({document: this.editorInternalStatus.uiDocumentItem, skipBack: true}));
  }
  public downloadWord() {
    if (this.editorInternalStatus.details) {
      if (this.localListChapters.length > 1) {
        this.service.getDocumentTemplateChapters(this.documentInfo.docId).subscribe((listTemplateChapterResponse: ListTemplateChapterResponse) => {
          this.dialogService.runSelectTemplateChapterDialog({ chapters: listTemplateChapterResponse.chapters }, 'Download Word', 'MESSAGE.DOWNLOAD_SELECT_CHAPTER_WORD').subscribe(
            (chapters?: Chapter[]) => {
              if (chapters) {
                this.downloadWordConfirmed(chapters);
              }
            });
        });
      } else {
        this.downloadWordConfirmed();
      }
    }
  }

  private downloadWordConfirmed(chapters?: Chapter[]) {
    this.editorInternalStatus.setLoadingWord(true);
    const request: DownloadRequest = chapters ? { chapterSelectors: chapters.map(c => c.selector) } : { chapterSelectors: [] };
    this.service.downloadWord(
      this.editorInternalStatus.details?.docId,
      request,
      this.editorInternalStatus.details?.name).subscribe(
      () => {
        this.editorInternalStatus.setLoadingWord(false);
      }, () => {
        this.editorInternalStatus.setLoadingWord(false);
      }
    );
  }

  public previewDocument() {
    if (this.editorInternalStatus.details) {
      if (this.localListChapters.length > 1) {
        this.service.getDocumentTemplateChapters(this.documentInfo.docId).subscribe((listTemplateChapterResponse: ListTemplateChapterResponse) => {
          this.dialogService.runSelectTemplateChapterDialog({chapters: listTemplateChapterResponse.chapters}).subscribe(
            (chapters?: Chapter[]) => {
              if (chapters) {
                this.previewConfirmed(chapters);
              }
            });
        })
      } else {
        this.previewConfirmed();
      }
    }
  }

  changeChapterStatus(status: ChapterStatus) {
    if (this.currentActiveChapter.currentStatus !== status) {
      const docId = this.documentInfo.docId;
      const chapterId = this.currentActiveChapter.id;
      const request: SetChapterStatusRequest = {
        chapter: this.currentActiveChapter.selector,
        status,
        completed: this.currentActiveChapter.completed
      }
      this.store.dispatch(NgrxWidgetActions.setChapterStatus({docId, chapterId, request}));
    }
  }

  changeViewMode(viewMode: ViewMode) {
    this.store.dispatch(NgrxWidgetActions.setViewMode({viewMode}));
  }

  updateLink(document: CommonDocumentEditorDetails) {
    const applicationLink: EditorLink = {
      title: this.translateService.stream('LABELS.APPLICATION'),
      label: of(document?.applicationName),
      link: AppLinkBuilder.linkOfEntity('applications', document?.applicationId),
      validated: false,
      newTab: true,
      enabled: true
    };
    this.links = [applicationLink];
  }

  private checkInitializedTemplateStatus(enableUpdateStatus = false) {
    if (this.editorInternalStatus.editorReady && this.editorInternalStatus.documentPermission?.writeAccess && this.currentActiveChapter) {
      // Ho finito di caricare tutto e posso fare modifiche nel documento

      const doc: CommonDocumentEditorDetails = this.editorInternalStatus.details;
      const userCheck: boolean = doc.userCheck || false;
      const documentStatus: DocResourceStatus = doc.status;
      const totalWidget = this.status?.totalWidget || 0;
      const validWidget = this.status?.validWidgetId?.length || 0;
      const completedSavedValue = this.currentActiveChapter.completed;
      const completedCurrentValue: number = EditorInternalStatus.calculatePercentageOfCompletion(totalWidget, validWidget);
      const chapterId = this.currentActiveChapter.id
      const chapterStatus = this.currentActiveChapter.currentStatus;

      const docId = doc.docId;

      switch (true) {
        case documentStatus === DocResourceStatus.PUBLISHED && userCheck:
          this.store.dispatch(NgrxWidgetActions.userCheckTrueInPublishedDocument({document: doc}));
          break;
        case documentStatus === DocResourceStatus.DRAFT && userCheck:
          /**
           * La rimozione della user Check è diventata esplicita e non più automatica
           * this.store.dispatch(NgrxWidgetActions.userCheckTrueInDraftDocument({document: doc}));
           */
          break;

        case enableUpdateStatus && EditorInternalStatus.isChangeCompletedStatus(totalWidget, validWidget, completedSavedValue): {
          const request: SetChapterStatusRequest = {
            chapter: this.currentActiveChapter.selector,
            status: chapterStatus,
            completed: completedCurrentValue
          }

          /**
           * Allineo le variabili interne perchè il metodo viene richiamato anche prima di aver aggiornato lo stato
           */
          this.currentActiveChapter.completed = completedCurrentValue;
          this.currentActiveChapter.currentStatus = chapterStatus;

          this.store.dispatch(NgrxWidgetActions.setChapterStatus({
            docId: docId,
            chapterId: chapterId,
            request: request
          }));
          break
        }

      }
    }
  }

  private reopenDocument() {
    this.store.dispatch(AiAssessmentActions.reopenAiAssessmentWithConfirm({id: this.editorInternalStatus.details?.docId}));
  }

  @Confirm({
    title: 'TITLE.AI_ASSESSMENT_DELETE',
    message: 'MESSAGE.AI_ASSESSMENT_DELETE',
    size: 'md'
  })
  private deleteDocument() {
    this.store.dispatch(AiAssessmentActions.deleteAiAssessment({id: this.documentInfo.docId,}));
  }

  private previewConfirmed(chapters?: Chapter[]) {
    this.editorInternalStatus.setLoadingPreview(true);
    const request: DownloadRequest = chapters ? {chapterSelectors: chapters.map(c => c.selector)} : {chapterSelectors: []};
    this.service.downloadPreviewPdf(this.editorInternalStatus.details?.docId, request,
      this.editorInternalStatus.details?.name).subscribe(
      (response: DownloadResponse) => {
        this.editorInternalStatus.setLoadingPreview(false);
        const dialog = this.widgetDialogService.runPdfPreview(of(this.editorInternalStatus.details?.name), response);
        dialog.subscribe();
      }, () => {
        this.editorInternalStatus.setLoadingPreview(false);
      }
    );
  }

  public onChangeNoChapterPermission($event: boolean) {
    this.editorInternalStatus.setNoChapterPermission($event);
    this.checkInitializedTemplateStatus(true);
  }
}
